import { z } from 'zod'

import { CreateThreadCommentSchema } from './column-definition/thread-comment'
import { TaskSchema } from './task'
import { TaskStatusTypeEnum } from './task-status'
import { ThreadSchema } from './thread'
import {
  ThreadCommentReactionSchema,
  ThreadReactionSchema,
} from './thread-reaction'

// Socket.io events
export enum SocketEvent {
  // Connection events
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  CONNECT_ERROR = 'connect_error',

  // Room events
  JOIN_THREAD = 'join_thread',
  LEAVE_THREAD = 'leave_thread',
  JOIN_TASK = 'join_task',
  LEAVE_TASK = 'leave_task',
  JOIN_PROJECT = 'join_project',
  LEAVE_PROJECT = 'leave_project',
  JOIN_USER = 'join_user',
  LEAVE_USER = 'leave_user',
  JOIN_LEAD = 'join_lead',
  LEAVE_LEAD = 'leave_lead',
  JOIN_DEAL = 'join_deal',
  LEAVE_DEAL = 'leave_deal',
  JOIN_CONTACT = 'join_contact',
  LEAVE_CONTACT = 'leave_contact',
  JOIN_FORM = 'join_form',
  LEAVE_FORM = 'leave_form',
  JOIN_COLLECTION = 'join_collection',
  LEAVE_COLLECTION = 'leave_collection',
  JOIN_MEMBER = 'join_member',
  LEAVE_MEMBER = 'leave_member',
  JOIN_COLLECTION_ENTRY = 'join_collection_entry',
  LEAVE_COLLECTION_ENTRY = 'leave_collection_entry',
  JOIN_WORKFLOW = 'join_workflow',
  LEAVE_WORKFLOW = 'leave_workflow',

  // Thread events
  THREAD_CREATED = 'thread:created',
  THREAD_UPDATED = 'thread:updated',
  THREAD_DELETED = 'thread:deleted',
  THREAD_COMMENT_CREATED = 'thread:comment_created',
  THREAD_COMMENT_UPDATED = 'thread:comment_updated',
  THREAD_COMMENT_DELETED = 'thread:comment_deleted',
  THREAD_MESSAGE_RECEIVED = 'thread:message_received', // For native notifications
  THREAD_MEMBER_ADDED = 'thread:member_added', // Notify user when added to a thread

  // Reaction events (specific events for threads and comments)
  THREAD_REACTION_ADDED = 'thread_reaction:added',
  THREAD_REACTION_REMOVED = 'thread_reaction:removed',
  COMMENT_REACTION_ADDED = 'comment_reaction:added',
  COMMENT_REACTION_REMOVED = 'comment_reaction:removed',

  // Task events
  TASK_CREATED = 'task:created',
  TASK_UPDATED = 'task:updated',
  TASK_DELETED = 'task:deleted',
  TASK_STATUS_UPDATED = 'task:status_updated',
  TASK_STATUS_CHANGED = 'task:status_changed',
  TASK_ASSIGNED = 'task:assigned',
  TASK_UNASSIGNED = 'task:unassigned',
  TASK_ASSIGNEE_ADDED = 'task:assignee_added',
  TASK_ASSIGNEE_REMOVED = 'task:assignee_removed',
  RECURRING_TASK_CREATED = 'task:recurring_created',
  TASK_COMMENT_COUNT_UPDATED = 'task:comment_count_updated',

  // Time entry events
  TIME_ENTRY_STARTED = 'time_entry:started',
  TIME_ENTRY_STOPPED = 'time_entry:stopped',
  TIME_ENTRY_PAUSED = 'time_entry:paused',
  TIME_ENTRY_RESUMED = 'time_entry:resumed',
  TIME_ENTRY_UPDATED = 'time_entry:updated',
  TIME_ENTRY_DELETED = 'time_entry:deleted',
  // Org-room presence ping when any member's timer changes state.
  // Deliberately slim ({ userId, action, at }) — no entry data; viewers
  // refetch through the permission-checked team summary endpoint.
  TEAM_TIME_ACTIVITY = 'team_time:activity',

  // Meeting recording events (emitted to the creator's user room)
  MEETING_RECORDING_UPDATED = 'meeting_recording:updated',

  // AI-assistant-dispatched client actions (emitted to the requesting
  // user's room). Some assistant tools act on device-local capabilities the
  // server can't reach (e.g. the desktop meeting recorder) — the tool emits
  // an action and the desktop main window executes it.
  ASSISTANT_CLIENT_ACTION = 'assistant:client_action',

  // Note events
  JOIN_NOTE = 'join_note',
  LEAVE_NOTE = 'leave_note',
  NOTE_CREATED = 'note:created',
  NOTE_UPDATED = 'note:updated',
  NOTE_DELETED = 'note:deleted',
  NOTE_SYNC = 'note:sync',
  NOTE_SAVED = 'note:saved',
  NOTE_AWARENESS = 'note:awareness',

  // Note page events (for collection notes)
  JOIN_NOTE_PAGE = 'join_note_page',
  LEAVE_NOTE_PAGE = 'leave_note_page',
  NOTE_PAGE_CREATED = 'note_page:created',
  NOTE_PAGE_UPDATED = 'note_page:updated',
  NOTE_PAGE_DELETED = 'note_page:deleted',
  NOTE_PAGE_SYNC = 'note_page:sync',
  NOTE_PAGE_SAVED = 'note_page:saved',
  NOTE_PAGE_AWARENESS = 'note_page:awareness',
  NOTE_PAGE_USER_LEFT = 'note_page:user_left',

  // Note-level presence (for tracking all users in a collection)
  NOTE_PRESENCE_UPDATE = 'note_presence:update',
  NOTE_PRESENCE_SYNC = 'note_presence:sync',
  NOTE_PRESENCE_LEAVE = 'note_presence:leave',

  // Note page list events (for sidebar updates without disrupting focus)
  NOTE_PAGES_REORDERED = 'note_pages:reordered',

  // Direct Message / Conversation events
  JOIN_CONVERSATION = 'join_conversation',
  LEAVE_CONVERSATION = 'leave_conversation',
  MESSAGE_CREATED = 'message:created',
  MESSAGE_UPDATED = 'message:updated',
  MESSAGE_DELETED = 'message:deleted',
  MESSAGE_READ = 'message:read',
  CONVERSATION_CREATED = 'conversation:created',
  CONVERSATION_UPDATED = 'conversation:updated',
  TYPING_INDICATOR = 'typing:indicator',

  // Client Portal events
  JOIN_CLIENT_PORTAL = 'join_client_portal',
  LEAVE_CLIENT_PORTAL = 'leave_client_portal',
  CLIENT_PORTAL_TASK_CREATED = 'client_portal:task_created',
  CLIENT_PORTAL_TASK_UPDATED = 'client_portal:task_updated',
  CLIENT_PORTAL_TASK_DELETED = 'client_portal:task_deleted',
  CLIENT_PORTAL_TASK_STATUS_UPDATED = 'client_portal:task_status_updated',
  CLIENT_PORTAL_RESOURCE_UPDATED = 'client_portal:resource_updated',

  // Cycle events
  JOIN_CYCLE = 'join_cycle',
  LEAVE_CYCLE = 'leave_cycle',
  CYCLE_TEMPLATE_CREATED = 'cycle:template_created',
  CYCLE_TEMPLATE_UPDATED = 'cycle:template_updated',
  CYCLE_TEMPLATE_DELETED = 'cycle:template_deleted',
  CYCLE_INSTANCE_CREATED = 'cycle:instance_created',
  CYCLE_INSTANCE_UPDATED = 'cycle:instance_updated',
  CYCLE_INSTANCE_DELETED = 'cycle:instance_deleted',
  CYCLE_INSTANCE_STARTED = 'cycle:instance_started',
  CYCLE_INSTANCE_COMPLETED = 'cycle:instance_completed',
  CYCLE_INSTANCE_CANCELLED = 'cycle:instance_cancelled',

  // Sprint events
  SPRINT_CREATED = 'sprint:created',
  SPRINT_UPDATED = 'sprint:updated',
  SPRINT_DELETED = 'sprint:deleted',
  SPRINT_STARTED = 'sprint:started',
  SPRINT_COMPLETED = 'sprint:completed',

  // Bucket events (statusless — no started/completed)
  BUCKET_CREATED = 'bucket:created',
  BUCKET_UPDATED = 'bucket:updated',
  BUCKET_DELETED = 'bucket:deleted',

  // Task Group events
  TASK_GROUP_CREATED = 'task_group:created',
  TASK_GROUP_UPDATED = 'task_group:updated',
  TASK_GROUP_DELETED = 'task_group:deleted',
  TASK_GROUP_ORDER_CHANGED = 'task_group:order_changed',

  // Task status configuration events (per-module status definitions:
  // names, colors, order — emitted when a module's statuses are set or reset)
  TASK_STATUSES_UPDATED = 'task_status:config_updated',

  // Workflow events
  WORKFLOW_CREATED = 'workflow:created',
  WORKFLOW_UPDATED = 'workflow:updated',
  WORKFLOW_DELETED = 'workflow:deleted',
  WORKFLOW_RUN_CREATED = 'workflow_run:created',
  WORKFLOW_RUN_UPDATED = 'workflow_run:updated',

  // User Preferences events (for cross-window sync)
  USER_PREFERENCES_UPDATED = 'user_preferences:updated',

  // Invoice changed (payment, refund, status, online payment attempt).
  // Emitted to the organization room with routing ids only
  // (InvoiceUpdatedEvent); viewers refetch through permission-checked reads.
  INVOICE_UPDATED = 'invoice:updated',

  // Inventory — room join/leave for item detail view
  JOIN_INVENTORY_ITEM = 'join_inventory_item',
  LEAVE_INVENTORY_ITEM = 'leave_inventory_item',

  // Inventory — item CRUD
  INVENTORY_ITEM_CREATED = 'inventory:item_created',
  INVENTORY_ITEM_UPDATED = 'inventory:item_updated',
  INVENTORY_ITEM_DELETED = 'inventory:item_deleted',

  // Inventory — per-item stock change (emitted in addition to item_updated
  // so listeners that only care about quantity can skip full-row processing)
  INVENTORY_QUANTITY_CHANGED = 'inventory:quantity_changed',

  // Inventory — movements (immutable audit ledger)
  INVENTORY_MOVEMENT_RECORDED = 'inventory:movement_recorded',

  // Inventory — adjustments
  INVENTORY_ADJUSTMENT_CREATED = 'inventory:adjustment_created',
  INVENTORY_ADJUSTMENT_APPROVED = 'inventory:adjustment_approved',
  INVENTORY_ADJUSTMENT_REJECTED = 'inventory:adjustment_rejected',

  // Inventory — categories
  INVENTORY_CATEGORY_CREATED = 'inventory:category_created',
  INVENTORY_CATEGORY_UPDATED = 'inventory:category_updated',
  INVENTORY_CATEGORY_DELETED = 'inventory:category_deleted',

  // Inventory — locations
  INVENTORY_LOCATION_CREATED = 'inventory:location_created',
  INVENTORY_LOCATION_UPDATED = 'inventory:location_updated',
  INVENTORY_LOCATION_DELETED = 'inventory:location_deleted',

  // Project collection/detail events
  PROJECT_CREATED = 'project:created',
  PROJECT_UPDATED = 'project:updated',
  PROJECT_DELETED = 'project:deleted',
  PROJECT_STATUS_UPDATED = 'project:status:updated',
  PROJECT_MEMBER_ADDED = 'project:member:added',
  PROJECT_MEMBER_REMOVED = 'project:member:removed',

  // Lead events
  LEAD_CREATED = 'lead:created',
  LEAD_UPDATED = 'lead:updated',
  LEAD_DELETED = 'lead:deleted',

  // Deal events
  DEAL_CREATED = 'deal:created',
  DEAL_UPDATED = 'deal:updated',
  DEAL_DELETED = 'deal:deleted',

  // Contact events
  CONTACT_CREATED = 'contact:created',
  CONTACT_UPDATED = 'contact:updated',
  CONTACT_DELETED = 'contact:deleted',

  // Entity events
  ENTITY_CREATED = 'entity:created',
  ENTITY_UPDATED = 'entity:updated',
  ENTITY_DELETED = 'entity:deleted',

  // Ticket events
  TICKET_CREATED = 'ticket:created',
  TICKET_UPDATED = 'ticket:updated',
  TICKET_DELETED = 'ticket:deleted',
  TICKET_COMMUNICATION_CREATED = 'ticket:communication_created',

  // Form events
  FORM_CREATED = 'form:created',
  FORM_UPDATED = 'form:updated',
  FORM_DELETED = 'form:deleted',

  // Collection events
  COLLECTION_CREATED = 'collection:created',
  COLLECTION_UPDATED = 'collection:updated',
  COLLECTION_DELETED = 'collection:deleted',

  // Collection entry events
  COLLECTION_ENTRY_CREATED = 'collectionEntry:created',
  COLLECTION_ENTRY_UPDATED = 'collectionEntry:updated',
  COLLECTION_ENTRY_DELETED = 'collectionEntry:deleted',

  // Member events
  MEMBER_CREATED = 'member:created',
  MEMBER_UPDATED = 'member:updated',
  MEMBER_DELETED = 'member:deleted',

  // Event (calendar) events
  EVENT_CREATED = 'event:created',
  EVENT_UPDATED = 'event:updated',
  EVENT_DELETED = 'event:deleted',

  // Document events
  DOCUMENT_CREATED = 'document:created',
  DOCUMENT_UPDATED = 'document:updated',
  DOCUMENT_DELETED = 'document:deleted',
  DOCUMENT_ACL_CHANGED = 'document:acl-changed',

  // Planner events (staffing / allocation planning)
  PLANNER_ALLOCATION_CREATED = 'planner:allocation.created',
  PLANNER_ALLOCATION_UPDATED = 'planner:allocation.updated',
  PLANNER_ALLOCATION_TRANSITIONED = 'planner:allocation.transitioned',
  PLANNER_ALLOCATION_DELETED = 'planner:allocation.deleted',
  PLANNER_ALLOCATION_EDITING = 'planner:allocation.editing',
  PLANNER_STAFFING_DECISION_CREATED = 'planner:staffing-decision.created',

  // Member resource — rates
  MEMBER_RESOURCE_RATE_CREATED = 'member-resource:rate:created',
  MEMBER_RESOURCE_RATE_UPDATED = 'member-resource:rate:updated',
  MEMBER_RESOURCE_RATE_DELETED = 'member-resource:rate:deleted',

  // Member resource — allocations
  MEMBER_RESOURCE_ALLOCATION_CREATED = 'member-resource:allocation:created',
  MEMBER_RESOURCE_ALLOCATION_UPDATED = 'member-resource:allocation:updated',
  MEMBER_RESOURCE_ALLOCATION_DELETED = 'member-resource:allocation:deleted',
  MEMBER_RESOURCE_ALLOCATION_CONFLICT = 'member-resource:allocation:conflict',

  // Member resource — earnings
  MEMBER_RESOURCE_EARNINGS_CREATED = 'member-resource:earnings:created',
  MEMBER_RESOURCE_EARNINGS_UPDATED = 'member-resource:earnings:updated',
  MEMBER_RESOURCE_EARNINGS_STATUS_CHANGED = 'member-resource:earnings:status-changed',

  // Member resource — skills
  MEMBER_RESOURCE_SKILL_ADDED = 'member-resource:skill:added',
  MEMBER_RESOURCE_SKILL_UPDATED = 'member-resource:skill:updated',
  MEMBER_RESOURCE_SKILL_REMOVED = 'member-resource:skill:removed',

  // Member resource — utilization
  MEMBER_RESOURCE_UTILIZATION_UPDATED = 'member-resource:utilization:updated',
  MEMBER_RESOURCE_UTILIZATION_CALCULATION_PROGRESS = 'member-resource:utilization:calculation-progress',
  MEMBER_RESOURCE_UTILIZATION_CALCULATION_COMPLETE = 'member-resource:utilization:calculation-complete',

  // Member resource — profile
  MEMBER_RESOURCE_PROFILE_CREATED = 'member-resource:profile:created',
  MEMBER_RESOURCE_PROFILE_UPDATED = 'member-resource:profile:updated',
}

// Socket type definitions
// Using a generic interface instead of extending Socket directly
export interface ExtendedSocket {
  hasListeners(event: string): boolean
  connected: boolean
  id: string
  io: any
  nsp: string
  disconnected: boolean

  connect(): this
  disconnect(): this
  emit(ev: string, ...args: any[]): boolean
  on(ev: string, callback: Function): this
  off(ev: string, listener?: Function): this
  once(ev: string, listener: Function): this
  onAny(listener: Function): this
  offAny(listener?: Function): this
  listeners(event: string): Function[]
  compress(compress: boolean): this
  volatile: this
  timeout(timeout: number): this
}

export interface SocketContextType {
  socket: ExtendedSocket | null
  isConnected: boolean
  connect: () => void
  disconnect: () => void
  emit?: <T = unknown>(event: string, ...args: T[]) => void
  on?: <T = unknown>(event: string, listener: (data: T) => void) => void
  off?: <T = unknown>(event: string, listener: (data: T) => void) => void
}

// Room payloads
export const JoinThreadPayloadSchema = z.object({
  threadId: z.string().uuid(),
})

export const JoinTaskPayloadSchema = z.object({
  taskId: z.string().uuid(),
})

export const JoinProjectPayloadSchema = z.object({
  projectId: z.string().uuid(),
})

export const JoinUserPayloadSchema = z.object({
  userId: z.string().uuid(),
})

export const JoinLeadPayloadSchema = z.object({
  leadId: z.string().uuid(),
})

export const JoinDealPayloadSchema = z.object({
  dealId: z.string().uuid(),
})

export const JoinContactPayloadSchema = z.object({
  contactId: z.string().uuid(),
})

export const JoinFormPayloadSchema = z.object({
  formId: z.string().uuid(),
})

export const JoinCollectionPayloadSchema = z.object({
  collectionId: z.string().uuid(),
})

export const JoinMemberPayloadSchema = z.object({
  memberId: z.string().uuid(),
})

export const JoinCollectionEntryPayloadSchema = z.object({
  collectionEntryId: z.string().uuid(),
})

export const JoinInventoryItemPayloadSchema = z.object({
  itemId: z.string().uuid(),
})
export type JoinInventoryItemPayload = z.infer<
  typeof JoinInventoryItemPayloadSchema
>

// Event payloads
export const ThreadCommentCreatedPayloadSchema = CreateThreadCommentSchema

export const TaskStatusUpdatedPayloadSchema = z.object({
  taskId: z.string().uuid(),
  status: z.nativeEnum(TaskStatusTypeEnum),
  updatedById: z.string().uuid(),
})

export const TaskCommentCountUpdatedPayloadSchema = z.object({
  taskId: z.string().uuid(),
  commentCount: z.number(),
})

// Emitted when a module's status configuration changes (set or reset).
// Carries only the module reference so listeners can invalidate caches.
export const TaskStatusesUpdatedPayloadSchema = z.object({
  organizationId: z.string().uuid(),
  moduleType: z.enum(['ORGANIZATION', 'PROJECT', 'TASK_GROUP', 'USER']),
  moduleId: z.string().nullable(),
})

// Reaction payload schemas

// -- Thread Reactions --
export const ThreadReactionAddedPayloadSchema = ThreadReactionSchema.extend({
  // Include creator info for immediate UI update without refetch
  creator: z
    .object({
      id: z.string().uuid(),
      displayName: z.string().nullable(),
      image: z.string().nullable(),
    })
    .optional(),
})

export const ThreadReactionRemovedPayloadSchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  creatorId: z.string().uuid(),
  emoji: z.string(), // Include emoji for UI update
})

// -- Comment Reactions --
export const CommentReactionAddedPayloadSchema =
  ThreadCommentReactionSchema.extend({
    threadId: z.string().uuid(),
    // Include creator info for immediate UI update without refetch
    creator: z
      .object({
        id: z.string().uuid(),
        displayName: z.string().nullable(),
        image: z.string().nullable(),
      })
      .optional(),
  })

export const CommentReactionRemovedPayloadSchema = z.object({
  id: z.string().uuid(),
  commentId: z.string().uuid(),
  threadId: z.string().uuid(),
  creatorId: z.string().uuid(),
  emoji: z.string(), // Include emoji for UI update
})

// Type definitions
export type JoinThreadPayload = z.infer<typeof JoinThreadPayloadSchema>
export type JoinTaskPayload = z.infer<typeof JoinTaskPayloadSchema>
export type JoinProjectPayload = z.infer<typeof JoinProjectPayloadSchema>
export type JoinUserPayload = z.infer<typeof JoinUserPayloadSchema>
export type JoinLeadPayload = z.infer<typeof JoinLeadPayloadSchema>
export type JoinDealPayload = z.infer<typeof JoinDealPayloadSchema>
export type JoinContactPayload = z.infer<typeof JoinContactPayloadSchema>
export type JoinFormPayload = z.infer<typeof JoinFormPayloadSchema>
export type JoinCollectionPayload = z.infer<typeof JoinCollectionPayloadSchema>
export type JoinMemberPayload = z.infer<typeof JoinMemberPayloadSchema>
export type JoinCollectionEntryPayload = z.infer<
  typeof JoinCollectionEntryPayloadSchema
>
export type ThreadCommentCreatedPayload = z.infer<
  typeof ThreadCommentCreatedPayloadSchema
>

export type ThreadReactionAddedPayload = z.infer<
  typeof ThreadReactionAddedPayloadSchema
>
export type ThreadReactionRemovedPayload = z.infer<
  typeof ThreadReactionRemovedPayloadSchema
>
export type CommentReactionAddedPayload = z.infer<
  typeof CommentReactionAddedPayloadSchema
>
export type CommentReactionRemovedPayload = z.infer<
  typeof CommentReactionRemovedPayloadSchema
>

export type TaskStatusUpdatedPayload = z.infer<
  typeof TaskStatusUpdatedPayloadSchema
>
export type TaskCommentCountUpdatedPayload = z.infer<
  typeof TaskCommentCountUpdatedPayloadSchema
>
export type TaskStatusesUpdatedPayload = z.infer<
  typeof TaskStatusesUpdatedPayloadSchema
>

// Note payload schemas
export const JoinNotePayloadSchema = z.object({
  noteId: z.string().uuid(),
})

export const YjsSyncPayloadSchema = z.object({
  noteId: z.string().uuid(),
  update: z.instanceof(Uint8Array),
  origin: z.string().optional(),
})

export const YjsAwarenessPayloadSchema = z.object({
  noteId: z.string().uuid(),
  added: z.array(z.number()),
  updated: z.array(z.number()),
  removed: z.array(z.number()),
  states: z.any(), // Map of client ID to awareness state
})

export type JoinNotePayload = z.infer<typeof JoinNotePayloadSchema>
export type YjsSyncPayload = z.infer<typeof YjsSyncPayloadSchema>
export type YjsAwarenessPayload = z.infer<typeof YjsAwarenessPayloadSchema>

// Note page payload schemas
export const JoinNotePagePayloadSchema = z.object({
  noteId: z.string().uuid(),
  pageId: z.string().uuid(),
})

export const YjsPageSyncPayloadSchema = z.object({
  noteId: z.string().uuid(),
  pageId: z.string().uuid(),
  update: z.instanceof(Uint8Array),
  origin: z.string().optional(),
})

export const YjsPageAwarenessPayloadSchema = z.object({
  noteId: z.string().uuid(),
  pageId: z.string().uuid(),
  added: z.array(z.number()),
  updated: z.array(z.number()),
  removed: z.array(z.number()),
  states: z.any(),
})

export type JoinNotePagePayload = z.infer<typeof JoinNotePagePayloadSchema>
export type YjsPageSyncPayload = z.infer<typeof YjsPageSyncPayloadSchema>
export type YjsPageAwarenessPayload = z.infer<
  typeof YjsPageAwarenessPayloadSchema
>

// Client auth for socket connection
export const SocketAuthSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid().optional(),
  token: z.string(),
})

export type SocketAuth = z.infer<typeof SocketAuthSchema>

// Client portal payload schemas
export const JoinClientPortalPayloadSchema = z.object({
  entityId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
})

export type JoinClientPortalPayload = z.infer<
  typeof JoinClientPortalPayloadSchema
>

// Cycle payload schemas
export const JoinCyclePayloadSchema = z.object({
  cycleInstanceId: z.string().uuid().optional(),
  cycleTemplateId: z.string().uuid().optional(),
  organizationId: z.string().uuid(),
})

export type JoinCyclePayload = z.infer<typeof JoinCyclePayloadSchema>

// Assistant client-action payload (SocketEvent.ASSISTANT_CLIENT_ACTION).
// Emitted to the requesting user's room when an assistant tool needs the
// desktop client to perform a device-local action.
/** Named app destinations the assistant may open (no record id needed). */
export type AssistantNavigatePage =
  | 'today'
  | 'my-tasks'
  | 'focus'
  | 'my-time'
  | 'tasks'
  | 'projects'
  | 'clients'
  | 'contacts'
  | 'leads'
  | 'deals'
  | 'tickets'
  | 'calendar'
  | 'notes'
  | 'documents'
  | 'invoices'
  | 'contracts'
  | 'expenses'
  | 'notifications'
  | 'threads'
  | 'forms'
  | 'settings'

export interface AssistantClientActionPayload {
  /** Unique event id — required by realtime payload validation. */
  id: string
  action: 'recording.start' | 'recording.stop' | 'navigate'
  /** Optional human title (e.g. meeting name) for recording.start. */
  title?: string
  /** navigate: open a specific record. Both fields required together. */
  moduleType?: string
  recordId?: string
  /** navigate: open a named page when no record is targeted. */
  page?: AssistantNavigatePage
}
